import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { useAppStore } from "../store/store";

export type HttpResult<T> =
  | { ok: true; data: T }
  | { ok: false; status?: number; message?: string };

let accessToken: string | null = null;

export const setAuthToken = (token: string | null) => {
  accessToken = token;
};

const api: AxiosInstance = axios.create({
  baseURL: "https://api-test.fixeraviation.com/api/",
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
      };
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      useAppStore.getState().authActions.logout();
    }
    return Promise.reject(error);
  },
);

const mapError = (e: unknown): HttpResult<any> => {
  const err = e as AxiosError<any>;
  return {
    ok: false,
    status: err.response?.status,
    message:
      err.response?.data?.message ||
      err.response?.statusText ||
      err.message ||
      "Network error",
  };
};

export const httpGet = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.get<T>(url, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export const httpPost = async <T>(
  url: string,
  body?: any,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.post<T>(url, body, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export const httpPut = async <T>(
  url: string,
  body?: any,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.put<T>(url, body, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export const httpDelete = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.delete<T>(url, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export const httpPatch = async <T>(
  url: string,
  body?: any,
  config?: AxiosRequestConfig,
): Promise<HttpResult<T>> => {
  try {
    const res = await api.patch<T>(url, body, config);
    return { ok: true, data: res.data };
  } catch (e) {
    return mapError(e);
  }
};

export { api };
